/**
 * Шаг 4 wizard «Параметры» (E07.5, ФТ-1.5).
 * Таблица формы по Figma Potok / node 1:931.
 */

import { useEffect, useMemo, useState } from "react";
import type { PublicFormField, PublicModelForm } from "@potok/shared";
import type { PublicCatalogApi } from "../../api/catalog.api";
import { ApiRequestError } from "../../api/http";
import selectChevron from "../../assets/select-chevron.svg";

export type ParamsStepProps = {
  api: PublicCatalogApi;
  modelId: string;
  /** Доп. подпись (напр. «модель 2 из 3»). */
  modelLabel?: string | undefined;
  values: Readonly<Record<string, string>>;
  onChange: (values: Record<string, string>) => void;
  onBack: () => void;
  onContinue: () => void;
};

export function ParamsStep({
  api,
  modelId,
  modelLabel,
  values,
  onChange,
  onBack,
  onContinue,
}: ParamsStepProps) {
  const [form, setForm] = useState<PublicModelForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSubmitError(null);

    api
      .getModelForm(modelId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setForm(loaded);
        const next = { ...values };
        let changed = false;
        for (const field of loaded.fields) {
          if (next[field.attributeId] === undefined) {
            next[field.attributeId] = field.defaultValue ?? "";
            changed = true;
          }
        }
        if (changed) {
          onChange(next);
        }
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(describeError(caught));
          setForm(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // defaults only on model load; values/onChange intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed defaults once per modelId
  }, [api, modelId]);

  const fields = useMemo(
    () => [...(form?.fields ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "ru")),
    [form],
  );

  const fillable = useMemo(
    () => fields.filter((field) => field.inputType !== "calculated" || field.allowManualOverride),
    [fields],
  );

  const filledCount = useMemo(
    () => fillable.filter((field) => fieldValue(values, field).trim() !== "").length,
    [fillable, values],
  );

  const fillRatio = fillable.length === 0 ? 0 : filledCount / fillable.length;

  function setFieldValue(attributeId: string, value: string): void {
    onChange({ ...values, [attributeId]: value });
    setSubmitError(null);
  }

  function handleContinue(): void {
    if (!form) {
      return;
    }
    const missing = form.fields.filter(
      (field) =>
        field.isRequired &&
        field.inputType !== "calculated" &&
        fieldValue(values, field).trim() === "",
    );
    if (missing.length > 0) {
      setSubmitError(`Заполните обязательные поля: ${missing.map((field) => field.name).join(", ")}`);
      return;
    }
    onContinue();
  }

  const modelName = form?.modelName ?? modelLabel ?? "—";

  return (
    <section className="wizard-step wizard-step--params" aria-labelledby="params-step-title">
      <header className="params-header">
        <div className="params-header__text">
          <h2 id="params-step-title" className="wizard-step__title">
            Параметры расчёта
          </h2>
          <p className="wizard-step__hint">
            Шаг 4 из 5 · Модель: <strong>{modelName}</strong>
            {modelLabel && form?.modelName ? (
              <span className="params-header__extra"> · {modelLabel}</span>
            ) : null}
          </p>
        </div>
        <ul className="params-legend" aria-label="Типы полей">
          <li className="params-legend__item">
            <span className="params-legend__icon" aria-hidden="true">
              ▾
            </span>
            Список
          </li>
          <li className="params-legend__item">
            <span className="params-legend__icon" aria-hidden="true">
              ✎
            </span>
            Ввод
          </li>
          <li className="params-legend__item">
            <span className="params-legend__icon params-legend__icon--calc" aria-hidden="true">
              ∑
            </span>
            Вычислено
          </li>
        </ul>
      </header>

      {error ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {error}
        </p>
      ) : null}
      {submitError ? (
        <p className="wizard-step__status wizard-step__status--error" role="alert">
          {submitError}
        </p>
      ) : null}
      {loading ? <p className="wizard-step__hint">Загрузка формы…</p> : null}

      {!loading && form ? (
        <>
          <div className="params-progress" aria-live="polite">
            <div className="params-progress__meta">
              <span>Заполнено полей</span>
              <span className="params-progress__count">
                {filledCount} / {fillable.length}
              </span>
            </div>
            <div
              className="params-progress__track"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={fillable.length}
              aria-valuenow={filledCount}
              aria-label="Заполнено полей"
            >
              <div
                className="params-progress__fill"
                style={{ width: `${Math.round(fillRatio * 100)}%` }}
              />
            </div>
          </div>

          {fields.length === 0 ? (
            <p className="wizard-step__hint">Для этой модели пока нет полей формы</p>
          ) : (
            <div className="params-table" role="table" aria-label="Параметры расчёта">
              <div className="params-table__head" role="row">
                <div className="params-table__cell params-table__cell--param" role="columnheader">
                  Параметр
                </div>
                <div className="params-table__cell params-table__cell--value" role="columnheader">
                  Значение
                </div>
                <div className="params-table__cell params-table__cell--unit" role="columnheader">
                  Ед.
                </div>
              </div>
              {fields.map((field, index) => {
                const isCalc = field.inputType === "calculated";
                const rowClass = [
                  "params-table__row",
                  isCalc ? "params-table__row--calc" : null,
                  !isCalc && index % 2 === 1 ? "params-table__row--alt" : null,
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <div key={field.id} className={rowClass} role="row">
                    <div className="params-table__cell params-table__cell--param" role="cell">
                      {isCalc ? (
                        <span className="params-table__sigma" aria-hidden="true">
                          ∑
                        </span>
                      ) : null}
                      <label className="params-table__label" htmlFor={`param-${field.id}`}>
                        {field.name}
                        {field.isRequired && !isCalc ? (
                          <span className="params-field__required" aria-hidden="true">
                            *
                          </span>
                        ) : null}
                      </label>
                      {isCalc && field.formulaName ? (
                        <span className="params-table__formula">{field.formulaName}</span>
                      ) : null}
                    </div>
                    <div className="params-table__cell params-table__cell--value" role="cell">
                      <FieldControl
                        field={field}
                        value={fieldValue(values, field)}
                        onChange={(value) => setFieldValue(field.attributeId, value)}
                      />
                    </div>
                    <div className="params-table__cell params-table__cell--unit" role="cell">
                      {field.unitName ?? ""}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}

      <div className="wizard-step__actions wizard-step__actions--end">
        <button type="button" className="wizard-step__back" onClick={onBack}>
          ← Назад
        </button>
        <button
          type="button"
          className="wizard-step__primary"
          disabled={loading || form === null || error !== null}
          onClick={handleContinue}
        >
          Получить результат →
        </button>
      </div>
    </section>
  );
}

function FieldControl({
  field,
  value,
  onChange,
}: {
  field: PublicFormField;
  value: string;
  onChange: (value: string) => void;
}) {
  const id = `param-${field.id}`;

  if (field.inputType === "calculated") {
    const editable = field.allowManualOverride;
    return (
      <div className="params-field__control params-field__control--inline">
        <input
          id={id}
          className={editable ? "params-input params-input--calc" : "params-calc-value"}
          type="text"
          inputMode={field.valueType === "number" ? "decimal" : undefined}
          value={value}
          readOnly={!editable}
          placeholder={editable ? "введите" : "—"}
          onChange={(event) => onChange(event.target.value)}
          aria-describedby={`${id}-hint`}
        />
        <span id={`${id}-hint`} className="visually-hidden">
          {editable ? "Расчётное поле, можно скорректировать" : "Расчётное поле"}
        </span>
      </div>
    );
  }

  if (field.inputType === "list") {
    return (
      <div className="params-field__control params-field__control--inline params-select-wrap">
        <select
          id={id}
          className="params-input params-input--select"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          aria-label={field.name}
        >
          <option value="">— выбрать —</option>
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.value}
            </option>
          ))}
        </select>
        <img className="params-select-wrap__chevron" src={selectChevron} alt="" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className="params-field__control params-field__control--inline">
      <input
        id={id}
        className="params-input"
        type="text"
        inputMode={field.valueType === "number" ? "decimal" : undefined}
        value={value}
        placeholder="введите"
        onChange={(event) => onChange(event.target.value)}
        autoComplete="off"
        aria-label={field.name}
      />
    </div>
  );
}

function fieldValue(
  values: Readonly<Record<string, string>>,
  field: PublicFormField,
): string {
  if (values[field.attributeId] !== undefined) {
    return values[field.attributeId]!;
  }
  return field.defaultValue ?? "";
}

function describeError(caught: unknown): string {
  if (caught instanceof ApiRequestError) {
    return caught.message;
  }
  if (caught instanceof Error) {
    return caught.message;
  }
  return "Не удалось загрузить форму";
}
